'use client';

import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BodyDefault, MetaDefault } from '@terra/ui-design-system';
import { Box, Grid, HStack, VStack, token } from '@terra/ui-styled-system';
import { SankeyChart, type SankeyChartProps } from './SankeyChart';
import { orderSignalFlowLinksForNodeAppearance } from './signal-flow-sankey-link-order';
import {
  sankeyNodeLayoutMap,
  sankeyNodeLayoutsEqual,
  type SankeyNodeLayout,
} from './sankey-node-layout';

export type SignalFlowDatum = {
  sourceLabel: string;
  targetKey: string;
  targetLabel: string;
  count: number;
};

export type SignalFlowRef = Pick<SignalFlowDatum, 'sourceLabel' | 'targetKey'>;

export type SignalFlowSankeyProps = {
  flows: SignalFlowDatum[];
  hubLabel: string;
  height?: number;
  labelMarginLeft?: number;
  labelMarginRight?: number;
  sourceOrder?: readonly string[];
  targetOrder?: readonly string[];
  mutedTargetKeys?: readonly string[];
  renderSourceLabel?: (sourceLabel: string, count: number) => ReactNode;
  renderTargetLabel?: (
    targetKey: string,
    targetLabel: string,
    count: number,
  ) => ReactNode;
  onSourceClick?: (sourceLabel: string) => void;
  onTargetClick?: (targetKey: string) => void;
  onFlowClick?: (flow: SignalFlowRef) => void;
};

function formatCount(value: number): string {
  return new Intl.NumberFormat().format(value);
}

function stringListKey(values?: readonly string[]): string {
  return values?.join('\0') ?? '';
}

function flowsLayoutKey(flows: SignalFlowDatum[]): string {
  return flows
    .map((flow) => `${flow.sourceLabel}\0${flow.targetKey}\0${flow.count}`)
    .join('\n');
}

function compareByOrderOrCount<T extends { count: number }>({
  left,
  right,
  order,
  orderKey,
}: {
  left: T;
  right: T;
  order?: readonly string[];
  orderKey: (item: T) => string;
}): number {
  if (order) {
    return order.indexOf(orderKey(left)) - order.indexOf(orderKey(right));
  }

  return right.count - left.count;
}

export function SignalFlowSankey({
  flows,
  hubLabel,
  height = 300,
  labelMarginLeft = 150,
  labelMarginRight = 180,
  sourceOrder,
  targetOrder,
  mutedTargetKeys,
  renderSourceLabel,
  renderTargetLabel,
  onSourceClick,
  onTargetClick,
  onFlowClick,
}: SignalFlowSankeyProps) {
  const [nodeCenters, setNodeCenters] = useState<Record<string, number>>({});
  const handleNodeLayouts = useCallback((layouts: SankeyNodeLayout[]) => {
    const next = sankeyNodeLayoutMap(layouts);

    setNodeCenters((previous) =>
      sankeyNodeLayoutsEqual(previous, next) ? previous : next,
    );
  }, []);
  const layoutResetKey = useMemo(
    () =>
      [
        flowsLayoutKey(flows),
        stringListKey(sourceOrder),
        stringListKey(targetOrder),
      ].join('\n'),
    [flows, sourceOrder, targetOrder],
  );

  useEffect(() => {
    setNodeCenters({});
  }, [layoutResetKey]);

  const { data, nodes, sources, targets } = useMemo<{
    data: SankeyChartProps['data'];
    nodes: SankeyChartProps['nodes'];
    sources: Array<{ label: string; count: number }>;
    targets: Array<{ key: string; label: string; count: number }>;
  }>(() => {
    const mutedTargets = new Set(mutedTargetKeys ?? []);
    const sourceCounts = new Map<string, number>();
    const targetCounts = new Map<string, { label: string; count: number }>();

    flows.forEach((flow) => {
      sourceCounts.set(
        flow.sourceLabel,
        (sourceCounts.get(flow.sourceLabel) ?? 0) + flow.count,
      );
      const target = targetCounts.get(flow.targetKey);

      targetCounts.set(flow.targetKey, {
        label: flow.targetLabel,
        count: (target?.count ?? 0) + flow.count,
      });
    });

    const sourceRows = Array.from(sourceCounts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((left, right) =>
        compareByOrderOrCount({
          left,
          right,
          order: sourceOrder,
          orderKey: (row) => row.label,
        }),
      );
    const targetRows = Array.from(targetCounts.entries())
      .map(([key, target]) => ({ key, ...target }))
      .sort((left, right) =>
        compareByOrderOrCount({
          left,
          right,
          order: targetOrder,
          orderKey: (row) => row.key,
        }),
      );
    const links: SankeyChartProps['data'] =
      orderSignalFlowLinksForNodeAppearance({
        flows,
        sourceLabels: sourceRows.map((source) => source.label),
        targetKeys: targetRows.map((target) => target.key),
      }).map(
        (flow) =>
          [
            flow.sourceLabel,
            flow.targetKey,
            flow.count,
          ] satisfies SankeyChartProps['data'][number],
      );
    const sourceColor = token('colors.bg.graphs.low');
    const targetColor = token('colors.fg.neutral.success');
    const otherTargetColor = token('colors.bg.graphs.gray');
    const sankeyNodes: SankeyChartProps['nodes'] = [
      ...sourceRows.map((source) => ({
        id: source.label,
        name: source.label,
        color: sourceColor,
        column: 0,
        visible: true,
      })),
      ...targetRows.map((target) => ({
        id: target.key,
        name: target.label,
        color: mutedTargets.has(target.key) ? otherTargetColor : targetColor,
        column: 1,
        visible: true,
      })),
    ];

    return {
      data: links,
      nodes: sankeyNodes,
      sources: sourceRows,
      targets: targetRows,
    };
  }, [flows, mutedTargetKeys, sourceOrder, targetOrder]);

  return (
    <VStack gap={2} alignItems="stretch" w="full">
      <Box ml={`${labelMarginLeft}px`}>
        <BodyDefault color="fg.default">{hubLabel}</BodyDefault>
      </Box>
      <Grid
        style={{
          gridTemplateColumns: `${labelMarginLeft}px minmax(0, 1fr) ${labelMarginRight}px`,
          height: `${height}px`,
        }}
        alignItems="stretch"
      >
        <Box position="relative" h="full">
          {sources.map((source) => {
            const centerY = nodeCenters[source.label];

            return (
              <HStack
                key={source.label}
                gap={2}
                alignItems="center"
                minW={0}
                position="absolute"
                left={0}
                right={0}
                style={{
                  top: centerY === undefined ? undefined : `${centerY}px`,
                  transform:
                    centerY === undefined ? undefined : 'translateY(-50%)',
                  visibility: centerY === undefined ? 'hidden' : 'visible',
                }}
                cursor={onSourceClick ? 'pointer' : undefined}
                onClick={() => onSourceClick?.(source.label)}
              >
                <Box minW={0} flex={1}>
                  {renderSourceLabel?.(source.label, source.count) ?? (
                    <BodyDefault color="fg.default" truncate>
                      {source.label}
                    </BodyDefault>
                  )}
                </Box>
                {!renderSourceLabel ? (
                  <MetaDefault color="fg.muted" whiteSpace="nowrap">
                    {formatCount(source.count)}
                  </MetaDefault>
                ) : null}
              </HStack>
            );
          })}
        </Box>
        <Box minW={0}>
          <SankeyChart
            data={data}
            nodes={nodes}
            height={height}
            nodeWidth={8}
            chartMarginLeft={0}
            chartMarginRight={0}
            chartMarginTop={0}
            chartMarginBottom={0}
            nodeLabelFormatter={() => ''}
            onNodeLayouts={handleNodeLayouts}
            onNodeClick={
              onSourceClick || onTargetClick
                ? (nodeId) => {
                    if (targets.some((target) => target.key === nodeId)) {
                      onTargetClick?.(nodeId);
                    } else {
                      onSourceClick?.(nodeId);
                    }
                  }
                : undefined
            }
            onLinkClick={
              onFlowClick
                ? ({ from, to }) =>
                    onFlowClick({ sourceLabel: from, targetKey: to })
                : undefined
            }
          />
        </Box>
        <Box position="relative" h="full">
          {targets.map((target) => {
            const centerY = nodeCenters[target.key];

            return (
              <HStack
                key={target.key}
                gap={2}
                alignItems="center"
                position="absolute"
                left={0}
                right={0}
                style={{
                  top: centerY === undefined ? undefined : `${centerY}px`,
                  transform:
                    centerY === undefined ? undefined : 'translateY(-50%)',
                  visibility: centerY === undefined ? 'hidden' : 'visible',
                }}
                cursor={onTargetClick ? 'pointer' : undefined}
                onClick={() => onTargetClick?.(target.key)}
              >
                {renderTargetLabel?.(
                  target.key,
                  target.label,
                  target.count,
                ) ?? (
                  <>
                    <BodyDefault color="fg.default" truncate>
                      {target.label}
                    </BodyDefault>
                    <MetaDefault color="fg.muted" whiteSpace="nowrap">
                      {formatCount(target.count)}
                    </MetaDefault>
                  </>
                )}
              </HStack>
            );
          })}
        </Box>
      </Grid>
    </VStack>
  );
}
